import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdToggleOn, MdToggleOff, MdPersonAddAlt1, MdPlayCircle, MdImage, MdVideocam,
  MdOutlineRemoveRedEye, MdOutlineVisibilityOff
} from 'react-icons/md';

/* ------------------------------------------------------------------ */
/* Phone-ratio preview: design + safe-area guide + fixed name/photo frame */
/* ------------------------------------------------------------------ */
function StatusPreview({ imageUrl, videoUrl, mediaType = 'image', width = 150 }) {
  const height = Math.round((width * 20) / 9); // 2160 x 4800 == 9:20
  const isVideo = mediaType === 'video';
  return (
    <div
      style={{
        position: 'relative', width, height, borderRadius: 12, overflow: 'hidden',
        background: '#111', flexShrink: 0,
      }}
    >
      {isVideo && videoUrl
        ? <video src={videoUrl} poster={imageUrl || undefined} controls muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : imageUrl
        ? <img src={imageUrl} alt="design" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 11 }}>No design</div>}

      {isVideo && (
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', borderRadius: 8, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <MdPlayCircle color="#fff" size={14} />
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>Video</span>
        </div>
      )}

      {!isVideo && (
        <>
          {/* Safe-area guide (~75% — keep art inside) */}
          <div style={{
            position: 'absolute', left: '5%', right: '5%', top: '8%', bottom: '22%',
            border: '1px dashed rgba(255,255,255,0.5)', borderRadius: 6, pointerEvents: 'none',
          }} />

          {/* Fixed name + photo frame (what the app draws) */}
          <div style={{
            position: 'absolute', left: 8, right: 8, bottom: 8,
            background: 'rgba(255,255,255,0.95)', borderRadius: 10, padding: '6px 8px',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <div style={{
              width: width * 0.16, height: width * 0.16, borderRadius: '50%',
              background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#9ca3af', flexShrink: 0,
            }}>
              <MdPersonAddAlt1 size={width * 0.09} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: Math.max(8, width * 0.06), fontWeight: 700, color: '#111' }}>Your Name</div>
              <div style={{ fontSize: Math.max(7, width * 0.045), color: '#D71920', fontWeight: 600 }}>Business News</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Categories (tabs) manager                                           */
/* ------------------------------------------------------------------ */
function CategoriesCard({ categories, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', sort_order: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      await api.post('/admin/status/categories', {
        name: form.name, slug: form.slug || undefined, sort_order: Number(form.sort_order) || 0,
      });
      toast.success('Category added');
      setForm({ name: '', slug: '', sort_order: '' });
      setShowForm(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const save = async (id) => {
    try {
      await api.put(`/admin/status/categories/${id}`, {
        name: editForm.name, slug: editForm.slug, sort_order: Number(editForm.sort_order) || 0,
      });
      toast.success('Updated'); setEditingId(null); reload();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggle = async (cat) => {
    try { await api.put(`/admin/status/categories/${cat.id}`, { is_active: !cat.is_active }); reload(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/status/categories/${deleteModal.id}`);
      toast.success('Deleted');
      setDeleteModal(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div className="section-header">
        <h3>Category Tabs</h3>
        <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add</>}
        </button>
      </div>

      {showForm && (
        <form className="manage-form" onSubmit={create}>
          <div className="form-group mb-2">
            <input placeholder="Tab name (e.g. శుభోదయం)" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
          </div>
          <div className="form-group mb-2">
            <input placeholder="Slug (optional, e.g. good-morning)" value={form.slug}
              onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} />
          </div>
          <div className="form-group mb-2">
            <input type="number" placeholder="Sort order (lower = first)" value={form.sort_order}
              onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-sm btn-primary">Add</button>
        </form>
      )}

      <div className="status-categories-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
        {categories.map(cat => (
          <div
            key={cat.id}
            style={{
              padding: '10px 14px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              transition: 'all 0.15s ease',
              minHeight: '62px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
            }}
          >
            {editingId === cat.id ? (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '8px' }}>
                  <input
                    className="form-control"
                    style={{ fontSize: '13px', padding: '6px 10px' }}
                    value={editForm.name}
                    onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Category Name"
                  />
                  <input
                    className="form-control"
                    style={{ fontSize: '13px', padding: '6px 10px' }}
                    value={editForm.slug}
                    onChange={e => setEditForm(p => ({ ...p, slug: e.target.value }))}
                    placeholder="Slug"
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ fontSize: '13px', padding: '6px 10px' }}
                    value={editForm.sort_order}
                    onChange={e => setEditForm(p => ({ ...p, sort_order: e.target.value }))}
                    placeholder="Sort #"
                  />
                </div>
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-sm btn-primary" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => save(cat.id)}>
                    <MdCheck size={14} style={{ marginRight: 4 }} /> Save
                  </button>
                  <button className="btn btn-sm btn-ghost" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => setEditingId(null)}>
                    <MdClose size={14} style={{ marginRight: 4 }} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, paddingRight: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cat.name}
                    </span>
                    {!cat.is_active && (
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', textTransform: 'uppercase' }}>
                        Hidden
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    {(cat.slug || cat.name || '').toUpperCase()} · #{cat.sort_order || 0}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <button
                    onClick={() => toggle(cat)}
                    title={cat.is_active ? 'Hide Category' : 'Show Category'}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: cat.is_active ? '#F8FAFC' : '#F1F5F9',
                      color: cat.is_active ? '#334155' : '#94A3B8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {cat.is_active ? <MdOutlineRemoveRedEye size={16} /> : <MdOutlineVisibilityOff size={16} />}
                  </button>

                  <button
                    onClick={() => { setEditingId(cat.id); setEditForm({ name: cat.name, slug: cat.slug, sort_order: cat.sort_order }); }}
                    title="Edit Category"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                      color: '#334155',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <MdEdit size={16} />
                  </button>

                  <button
                    onClick={() => setDeleteModal(cat)}
                    title="Delete Category"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: '1px solid #FCA5A5',
                      backgroundColor: '#FEF2F2',
                      color: '#DC2626',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <MdDelete size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        {categories.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13px' }}>
            No categories yet
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Category</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this category?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.name}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Templates manager                                                   */
/* ------------------------------------------------------------------ */
const emptyForm = { title: '', category_id: '', media_type: 'image', image_url: '', video_url: '', thumbnail_url: '', tags: '', scheduled_date: '', sort_order: '', is_active: true };

function TemplatesCard({ categories }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (filterCat) params.category_id = filterCat;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/status/templates', { params });
      setTemplates(res.data.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [filterCat, filterStatus]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadDesign = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        // full design + a smaller thumbnail (same source file)
        const main = await api.post('/admin/upload/status-image', fd, cfg);
        let thumbUrl = '';
        try {
          const fd2 = new FormData(); fd2.append('file', file);
          const thumb = await api.post('/admin/upload/status-thumbnail', fd2, cfg);
          thumbUrl = thumb.data.data.url;
        } catch { /* thumbnail optional */ }
        setForm(p => ({ ...p, image_url: main.data.data.url, thumbnail_url: thumbUrl }));
        toast.success('Design uploaded');
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const uploadVideoDesign = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        const res = await api.post('/admin/upload/status-video', fd, cfg);
        setForm(p => ({ ...p, video_url: res.data.data.url, thumbnail_url: res.data.data.thumbnail_url || '' }));
        toast.success('Video uploaded');
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error('Pick a category');
    if (form.media_type === 'video') {
      if (!form.video_url) return toast.error('Upload a video');
    } else if (!form.image_url) {
      return toast.error('Upload a design');
    }
    try {
      await api.post('/admin/status/templates', {
        title: form.title || null,
        category_id: Number(form.category_id),
        media_type: form.media_type,
        image_url: form.image_url || null,
        video_url: form.media_type === 'video' ? form.video_url : null,
        thumbnail_url: form.thumbnail_url || null,
        tags: form.tags || null,
        scheduled_date: form.scheduled_date || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      });
      toast.success('Template added');
      setForm(emptyForm);
      setShowForm(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggle = async (t) => {
    try { await api.put(`/admin/status/templates/${t.id}`, { is_active: !t.is_active }); fetchTemplates(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/status/templates/${deleteModal.id}`);
      toast.success('Status template deleted successfully.');
      setDeleteModal(null);
      fetchTemplates();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div className="section-header">
        <h3>Status Templates</h3>
        <div className="flex-row items-center gap-2">
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Live">Live</option>
            <option value="Expired">Expired</option>
          </select>
          <select className="filter-select" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add Template</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={create} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {/* Left: live preview */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div className="flex-row gap-2">
              <button
                type="button"
                className={`btn btn-sm ${form.media_type === 'image' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => set('media_type', 'image')}
              >
                <MdImage /> Image
              </button>
              <button
                type="button"
                className={`btn btn-sm ${form.media_type === 'video' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => set('media_type', 'video')}
              >
                <MdVideocam /> Video
              </button>
            </div>
            <StatusPreview imageUrl={form.image_url} videoUrl={form.video_url} mediaType={form.media_type} width={170} />
            {form.media_type === 'video' ? (
              <>
                <button type="button" className="btn btn-sm btn-ghost" onClick={uploadVideoDesign} disabled={uploading}>
                  {uploading ? 'Uploading…' : (form.video_url ? 'Replace video' : 'Upload video')}
                </button>
                <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                  A poster thumbnail is auto-extracted from the first frame.
                </span>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-sm btn-ghost" onClick={uploadDesign} disabled={uploading}>
                  {uploading ? 'Uploading…' : (form.image_url ? 'Replace design' : 'Upload design')}
                </button>
                <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                  2160 × 4800 px. Keep art inside the dashed safe area; the name/photo card is added automatically.
                </span>
              </>
            )}
          </div>

          {/* Right: fields */}
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="form-group">
              <label>Category *</label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} required>
                <option value="">Select…</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Title (optional, internal)</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Shivaratri 2026" />
            </div>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="festival, trending" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Schedule date (for “Today’s special”)</label>
                <input type="datetime-local" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Sort order</label>
                <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} placeholder="0" />
              </div>
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active (visible in app)
            </label>
            <button type="submit" className="btn btn-primary mt-md" disabled={uploading}>Add Template</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-muted">Loading…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16, marginTop: 12 }}>
          {templates.map(t => {
            let statusBadge = null;
            if (t.status === 'Scheduled') statusBadge = <span className="badge" style={{background:'#FEF08A', color:'#854D0E'}}>🟡 Scheduled</span>;
            else if (t.status === 'Live') statusBadge = <span className="badge" style={{background:'#BBF7D0', color:'#166534'}}>🟢 Live</span>;
            else if (t.status === 'Draft') statusBadge = <span className="badge" style={{background:'#E5E7EB', color:'#374151'}}>⚪ Draft</span>;
            else if (t.status === 'Expired') statusBadge = <span className="badge badge-expired">🔴 Expired</span>;

            return (
            <div key={t.id} className="card" style={{ padding: 10 }}>
              <StatusPreview imageUrl={t.thumbnail_url || t.image_url} videoUrl={t.video_url} mediaType={t.media_type} width={130} />
              <div style={{ marginTop: 8 }}>
                <span className="badge">{t.category?.name || '—'}</span>
                {statusBadge && <span className="ml-2">{statusBadge}</span>}
                {t.scheduled_date && (
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    📅 {new Date(t.scheduled_date).toLocaleDateString()}
                    <br />
                    🕒 {new Date(t.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                {t.title && <div style={{ fontSize: 12, marginTop: 2 }}>{t.title}</div>}
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{t.share_count || 0} shares</div>
              </div>
              <div className="action-btns" style={{ marginTop: 6 }}>
                <button className="btn btn-sm btn-ghost" onClick={() => toggle(t)} title={t.is_active ? 'Hide' : 'Show'}>
                  {t.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(t)} title="Delete Template"><MdDelete /></button>
              </div>
            </div>
          )})}
          {templates.length === 0 && <p className="text-muted">No templates yet</p>}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Status Template</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this status template?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title || 'Untitled Template'}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function StatusManager() {
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try { const res = await api.get('/admin/status/categories'); setCategories(res.data.data); }
    catch { toast.error('Failed to load categories'); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Status Maker</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 340px) 1fr', gap: 20, alignItems: 'start' }} className="status-grid">
        <CategoriesCard categories={categories} reload={loadCategories} />
        <TemplatesCard categories={categories} />
      </div>
    </div>
  );
}
